import { truncate } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import Confirm from '../../../base/Confirm';
import ListView from '../../../base/ListView';
import { useDeleteCertificate } from '../../../hooks/api/certificates/useDeleteCertificate';
import { useFrequentPassageStickerTypes } from '../../../hooks/api/frequent-passage-sticker-types/useFrequentPassageStickerTypes';
import { useDeleteMaterialRequest } from '../../../hooks/api/material-requests/useDeleteMaterialRequest';
import {
  Certificate,
  CrewMember,
  Event,
  FrequentPassageStickerType,
  QueryListResult,
  Registration,
  User,
} from '../../../hooks/api/utils/types';
import { useFilterQuery } from '../../../hooks/useFilterQuery';
import { dateRender, defaultError } from '../../../libs/utils';
import { getFrequentPassageStickerTypes } from '../../../services/api/frequentPassageStickerTypes';
import { getRegistrationCrewMembers } from '../../../services/api/registrationCrewMembers';
import ReportDownloadButton from '../../admin/reporting/exports/ReportDownloadButton';
import CreateGatekeepingRequestButton from '../gatekeeping/CreateGatekeepingRequestButton';
import ReviewCertificateButton from './ReviewCertificateButton';
import { camelCaseToWords } from '@shared/utils/helpers';

type Props = {
  data: QueryListResult<Certificate>;
  filters: Record<string, any>;
  admin?: boolean;
  user?: undefined | User;
  customFilters?: any[];
  onFiltersChange: (filters: Record<string, any>) => void;
};

export default function CertificatesListView({
  data,
  admin = false,
  user,
  filters,
  customFilters = [],
  onFiltersChange,
}: Props) {
  const { mutate: deleteCertificate } = useDeleteCertificate();

  async function handleDelete(id: number) {
    await Confirm('Are you sure you want to delete this certificate? This action can not be undone.', {
      title: 'Delete certificate',
      confirmStyle: 'btn-danger',
      confirmText: 'Delete',
    });

    return deleteCertificate({ id });
  }

  let adminColumns: any[] = [];
  if (admin) {
    adminColumns = [
      {
        key: 'internalComment',
        value: 'Internal Comment',
        render: (item: Certificate) => <em>{item.internalComment}</em>,
      },
    ];
  }

  const listViewConf = {
    dataSet: data?.data,
    dataMeta: data?.meta,
    columns: [
      {
        key: 'companyUser.user.fullName',
        value: 'Employee',
        render: (item: Certificate) => (
          <>
            <strong>{item.companyUser?.user?.fullName}</strong>
            <br />
            <small>{item.companyUser?.company?.name}</small>
          </>
        ),
      },
      {
        key: 'type',
        value: 'Type',
        render: (item: Certificate) => (
          <>
            <strong>{camelCaseToWords(item.type)}</strong>
            <br />
            <small>{truncate(item.description, { length: 60 })}</small>
          </>
        ),
      },
      {
        key: 'status',
        value: 'Status',
        render: (certificate: Certificate) => <StatusBadge certificate={certificate} />,
      },
      ...adminColumns,
      {
        key: 'expiresAt',
        value: 'Expiration date',
        render: (item: Certificate) => (item.expiresAt ? dateRender(item.expiresAt) : null),
      },
    ],
    baseFilters: filters,
    filters: [
      {
        label: 'Status',
        key: 'status',
        icon: 'la-check',
        type: 'select',
        dataset: [
          {
            key: 'pending',
            value: 'Pending',
          },
          {
            key: 'approved',
            value: 'Approved',
          },
          {
            key: 'rejected',
            value: 'Rejected',
          },
          {
            key: 'expired',
            value: 'Expired',
          },
        ],
      },
      {
        label: 'Type',
        icon: 'la-tag',
        type: 'select',
        key: 'type',
        dataset: [
          {
            key: 'driversLicense',
            value: "Driver's License",
          },
          {
            key: 'safetyCertificate',
            value: 'Safety Certificate',
          },
          {
            key: 'other',
            value: 'Other',
          },
        ],
      },
      ...customFilters,
    ],
    actions: {
      deleteLink: true,
      deleteFunction: handleDelete,
      editLink: false,
      // detailsLink: true
      customLink: (item: Certificate) => rowActions(item, admin),
    },
    url: admin ? `/admin/certificates` : '/company/employees/' + user?.id + '/certificates',
  };

  return (
    <>
      {!data.data && <div className="alert alert-info">No certificates found...</div>}
      {data.data && <ListView config={listViewConf} handleFilterUpdate={onFiltersChange} />}
    </>
  );
}

function rowActions(item: Certificate, admin: boolean) {
  let buttons = [];

  // if (item.downloadUrl) {
  //     buttons.push(
  //         <a
  //             className="btn btn-outline-primary m-btn btn-sm  m-btn--icon m--margin-left-5"
  //             href={item.downloadUrl}
  //             download={true}
  //         >
  //             <span>
  //                 <i className="la la-download"/>
  //                 <span>Download Voucher</span>
  //             </span>
  //         </a>
  //     );
  // }

  if (admin && (item.canApprove || item.canReject)) {
    buttons.push(<ReviewCertificateButton certificate={item} />);
  }

  return buttons;
}

function StatusBadge({ certificate }: { certificate: Certificate }) {
  const statusColors = {
    pending: 'warning',
    rejected: 'danger',
    approved: 'success',
    expired: 'metal',
  };

  return (
    <span
      className={`m-badge m-badge--${statusColors[certificate.status]} m-badge--wide`}
      title={certificate.publicComment}
    >
      {certificate.status}
      {certificate.publicComment && '*'}
    </span>
  );
}
